import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { AGENT_INBOX_CHANGED, agents, type InboxItem } from "~/common/agent-run/agents";
import { api, type ReviewInboxItem } from "~/core/api";
import { article as articlePath } from "~/core/paths";
import { keys, read } from "~/core/query";
import { readingPath } from "~/modules/analytics/lib/loop";

export type TaskInboxItem = ReviewInboxItem | (InboxItem & { id: string; kind: "workflow" });

export function mergeInbox(reviews: ReviewInboxItem[], workflows: InboxItem[]): TaskInboxItem[] {
	return [
		...reviews,
		...workflows.map((item) => ({ ...item, id: `workflow:${item.context_id}`, kind: "workflow" as const })),
	].sort((a, b) => {
		const deadline = (value: string | null) => (value ? Date.parse(value) : Number.POSITIVE_INFINITY);
		return (
			deadline(a.deadline) - deadline(b.deadline) ||
			Date.parse(a.created_at) - Date.parse(b.created_at) ||
			a.id.localeCompare(b.id)
		);
	});
}

export function reviewPath(item: ReviewInboxItem): string {
	const base = `/w/${encodeURIComponent(item.entity_id)}`;
	if (item.kind === "page_review") return articlePath(item.entity_id, item.page_id!);
	if (item.kind === "distribution_review") {
		return `${readingPath(base, "progress")}?${new URLSearchParams({ work: item.id.slice("work:".length) })}`;
	}
	const params = new URLSearchParams({ tab: "prepare", page: item.page_id ?? "", platform: item.platform! });
	if (item.kind === "publishing_review") params.set("task", item.id.slice("platform:".length));
	return `${readingPath(base, "distribution")}?${params}`;
}

/** Counts and rows share one account-wide read; a failed source must not look like an empty inbox. */
export function useTaskInbox(org: string) {
	const queryClient = useQueryClient();
	const state = useQuery({
		queryKey: [...keys.org(org), "task-inbox"],
		queryFn: async () => {
			const [reviews, workflows] = await Promise.all([api.reviewInbox(org), agents.inbox(org)]);
			return { items: mergeInbox(reviews.items, workflows.items) };
		},
		...read,
		placeholderData: undefined,
		refetchInterval: 10_000,
		refetchOnWindowFocus: true,
	});
	const { refetch } = state;
	useEffect(() => {
		const refresh = () => {
			if (!document.hidden) void refetch({ cancelRefetch: false });
		};
		const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
			if (event.type === "updated" && event.action.type === "invalidate" && event.query.queryKey[0] === "entity")
				refresh();
		});
		document.addEventListener("visibilitychange", refresh);
		window.addEventListener(AGENT_INBOX_CHANGED, refresh);
		return () => {
			unsubscribe();
			document.removeEventListener("visibilitychange", refresh);
			window.removeEventListener(AGENT_INBOX_CHANGED, refresh);
		};
	}, [refetch, queryClient]);
	return state;
}
